import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
  MouseEvent,
  ReactNode,
  RefObject,
  Fragment,
  useCallback,
} from 'react';
import { connect } from 'react-redux';
import { Sticky, StickyContainer } from 'react-sticky';
import cn from 'classnames';

import { TableCheckItems } from 'CorePackage/MainModule/types/TableCheckItems';
import { Checkbox } from 'UIPackage/MainModule/components/Checkbox';
import { getSortIcon } from 'CorePackage/MainModule/common/getSortIcon';
import { num2str } from 'CorePackage/MainModule/common/num2str';
import { IUserOrganisation } from 'APIPackage/AccountModule/interfaces/IUserOrganisation';
import { TableWithExternalScroll } from 'UIPackage/MainModule/components/TableWithExternalScroll';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { getAnnounceHeight, getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import {
  getUserRoleIsOmbudsman,
  getUserRoleIsProsecutor,
  getUserRoleIsSuperProsecutor,
  getUserRoleIsSupervisor,
} from 'AuthPackage/MainModule/selectors/authSelectors';
import { getAccountOrganizationsData } from 'CorePackage/AccountModule/selectors/accountSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Tooltip } from 'UIPackage/MainModule/components/Tooltip';

import { ITableColumn } from '../interfaces/ITableColumn';
import styles from './Table.module.scss';
import { deleteObjectProperties } from '../../MainModule/common/deleteObjectProperties';

interface IData {
  id?: number | string;
  deleted?: boolean;
  isPm?: boolean;
  isTest?: boolean;
}

interface IRenderTbodyDataCell<T> {
  column: ITableColumn<T>;
  row: T;
}

interface ITableProps<T> {
  columns: ITableColumn<T>[];
  data: T[];
  itemsCheck?: TableCheckItems;
  columnStyles?: {
    readonly [key: string]: string;
  };
  checkedNames?: string[];
  knoOrganisations?: IUserOrganisation[];
  isSupervisor?: boolean;
  isProsecutor?: boolean;
  isSuperProsecutor?: boolean;
  isOmbudsman?: boolean;
  isPm?: boolean;
  isErknm?: boolean;
  totalCount?: number;
  headPlacement?: 'centre';
  alignBodyCell?: 'centre';
  announceHeight?: number;
  rowClassNames?: (
    data: T,
  ) => {
    [key: string]: boolean;
  };
  setItemsCheck?: Dispatch<SetStateAction<TableCheckItems>>;
  onSortChange?: (id: keyof T) => void;
  onSettingsButtonClick?: () => void;
  renderDropdown?: (data: T) => JSX.Element | null;
  handleLoadClick?: () => void;
  onRowDoubleClick?: (row: T) => (e: MouseEvent<HTMLTableRowElement>) => void;
}

function TableWithoutStore<T extends IData>({
  columns,
  data,
  itemsCheck,
  columnStyles,
  checkedNames,
  isErknm,
  knoOrganisations,
  isSupervisor,
  isProsecutor,
  isSuperProsecutor,
  isOmbudsman,
  isPm,
  totalCount,
  headPlacement,
  alignBodyCell,
  rowClassNames,
  setItemsCheck,
  onSortChange,
  onSettingsButtonClick,
  renderDropdown,
  handleLoadClick,
  onRowDoubleClick,
}: ITableProps<T>) {
  const [isAllItemsChecked, setIsAllItemsChecked] = useState(false);
  const checkedItemsNumber = itemsCheck ? Object.keys(itemsCheck).filter(key => itemsCheck[key]).length : 0;

  const onCheckboxAllChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const isChecked = e.target.checked;
      const newItemsCheck: TableCheckItems = {};

      itemsCheck &&
        Object.keys(itemsCheck).forEach(key => {
          newItemsCheck[key] = isChecked;
        });
      setItemsCheck && setItemsCheck(newItemsCheck);
    },
    [itemsCheck, setItemsCheck],
  );

  useEffect(() => {
    if (itemsCheck) {
      const keysArray = Object.keys(itemsCheck);
      const isEachItemTrue = keysArray.length ? keysArray.every(key => itemsCheck[key]) : false;

      setIsAllItemsChecked(isEachItemTrue);
    }
  }, [itemsCheck]);

  const handleSortClick = (e: MouseEvent<HTMLElement>) => {
    const { id } = e.currentTarget.dataset;

    onSortChange && onSortChange(id as keyof T);
  };

  const isRenderColumn = (column: ITableColumn<T>) => {
    const isSelected = typeof column.isSelected === 'boolean' ? column.isSelected : true;
    const isHidden = typeof column.isHidden === 'boolean' ? column.isHidden : false;

    return isSelected && !isHidden;
  };

  const renderTheadDataCell = (column: ITableColumn<T>) => {
    const { id, name, isDisabledSort, tooltip } = column;
    const columnId = id.toString()?.split('.')[0];

    const theadAttributes = {
      ...(headPlacement === 'centre' && { 'data-centred': true }),
    };
    const columnClassName = columnStyles && columnStyles[`Cell${columnId.charAt(0).toUpperCase()}${columnId.slice(1)}`];

    const sortLinkAttributes = {
      ...(isDisabledSort && { 'data-disable': true }),
    };
    const theadCellHighlightedClassName =
      columnStyles &&
      column.isTheadCellHighlighted &&
      columnStyles[`Cell${columnId.charAt(0).toUpperCase()}${columnId.slice(1)}TheadCellHighlighted`];

    return (
      <th
        className={`${styles.TheadCell} ${columnClassName} ${theadCellHighlightedClassName}`}
        key={columnId}
        {...theadAttributes}
      >
        <div
          className={styles.SortLink}
          data-id={id}
          onClick={!isDisabledSort ? handleSortClick : undefined}
          {...sortLinkAttributes}
        >
          {name}
          {getSortIcon(columns, id)}
        </div>
        {tooltip && (
          <span className={styles.TooltipWrapper}>
            <Tooltip
              overlayClassName={styles.Tooltip}
              placement="top"
              content={<div className={styles.TooltipContent}>{tooltip}</div>}
            >
              <span className={styles.TooltipIcon} />
            </Tooltip>
          </span>
        )}
      </th>
    );
  };

  const handleTableCheckboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setItemsCheck &&
        setItemsCheck({
          ...itemsCheck,
          [e.target.id]: e.target.checked,
        });
    },
    [itemsCheck, setItemsCheck],
  );

  const renderTbodyDataCell = ({ column, row }: IRenderTbodyDataCell<T>) => {
    const { id, dataColumnType } = column;
    const currentId = id.toString()?.split('.')[0];
    const rowClassKey = `Cell${currentId?.charAt(0).toUpperCase()}${currentId?.slice(1)}`;
    const highlightedRowClassKey = `Cell${currentId?.charAt(0).toUpperCase()}${currentId?.slice(1)}Highlighted`;

    const tbodyAttributes = {
      ...(dataColumnType === 'centre' && { 'data-centred': true }),
    };
    const columnClassName = columnStyles && columnStyles[rowClassKey];

    const isHighlighted = column.isDataCellHighlighted ? column.isDataCellHighlighted(row) : false;
    const highlightedClassName = columnStyles && isHighlighted && columnStyles[highlightedRowClassKey];

    const value = row[id];
    let node: ReactNode;
    // для string полей

    if (typeof value === 'string' || typeof value === 'number') {
      node = value;
    }
    // для object, где берем вложенный параметр для рендера
    if (typeof value === 'object' && value) {
      const currentValue = Object.entries(value);

      node = column.nestedId ? currentValue.find(item => item[0] === column.nestedId)?.[1] : null;
    }
    if (column.renderCell) {
      node = column.renderCell({
        id,
        value,
        row,
        isErknm,
        knoOrganisations,
        isProsecutor,
        isSuperProsecutor,
        isSupervisor,
        isOmbudsman,
        isPm: row.isPm || isPm,
        isTest: row.isTest,
      });
    }

    return (
      <td className={`${styles.Cell} ${columnClassName} ${highlightedClassName}`} key={currentId} {...tbodyAttributes}>
        {node}
      </td>
    );
  };

  const handleMainTableScroll = (innerRef?: RefObject<HTMLTableElement>) => () => {
    const thead = innerRef?.current?.querySelector('thead');

    if (thead) {
      thead.scrollLeft = innerRef?.current?.scrollLeft ?? 0;
    }
  };

  const TableElement = ({ innerRef }: { innerRef?: RefObject<HTMLTableElement> }) => (
    <table className={styles.Table} ref={innerRef} onScroll={handleMainTableScroll(innerRef)}>
      <StickyContainer>
        <Sticky disableCompensation disableHardwareAcceleration>
          {({ style, isSticky, wasSticky }) => {
            if (isSticky && !wasSticky) {
              // если не использовать setTimeout, thead.scrollLeft при смене isSticky не изменится
              setTimeout(() => {
                handleMainTableScroll(innerRef)();
              });
            }

            return (
              <thead
                style={{
                  ...deleteObjectProperties(style, ['top', 'left']),
                  top: 70,
                }}
                className={cn({ [styles.TheadSticky]: isSticky }, { [styles.Thead]: !isSticky })}
              >
                <tr className="shared-flex">
                  {itemsCheck && (
                    <th className={styles.CheckBoxThead}>
                      <Checkbox
                        id="templates-check-all"
                        onChange={onCheckboxAllChange}
                        checked={isAllItemsChecked}
                        theme="system"
                      />
                    </th>
                  )}
                  {columns.map(column => {
                    if (isRenderColumn(column)) {
                      return renderTheadDataCell(column);
                    }

                    return null;
                  })}
                  {(renderDropdown || onSettingsButtonClick) && (
                    <th className={styles.MenuThead}>
                      {onSettingsButtonClick && (
                        <button
                          title="Настроить колонки"
                          className={styles.SettingsButton}
                          onClick={onSettingsButtonClick}
                        />
                      )}
                    </th>
                  )}
                </tr>
              </thead>
            );
          }}
        </Sticky>
        <tbody>
          {data.map(row => {
            const trAttributes = {
              ...(alignBodyCell && { 'data-centre': true }),
              ...(rowClassNames && rowClassNames(row)),
            };

            return (
              <tr
                className={cn('shared-flex', styles.TBodyRow)}
                key={row.id}
                onDoubleClick={onRowDoubleClick && onRowDoubleClick(row)}
                {...trAttributes}
              >
                {itemsCheck && (
                  <td className={styles.Cell}>
                    <div className={styles.CheckboxBlock}>
                      <Checkbox
                        className={styles.Checkbox}
                        id={`${row.id}`}
                        onChange={handleTableCheckboxChange}
                        checked={(row.id && itemsCheck[row.id]) || false}
                        theme="system"
                      />
                      {row.id && itemsCheck[row.id] && (
                        <div className={styles.CheckboxTooltip}>{`${num2str(checkedItemsNumber, [
                          'Выбран',
                          'Выбрано',
                          'Выбрано',
                        ])} ${checkedItemsNumber} ${num2str(
                          checkedItemsNumber,
                          checkedNames || ['запись', 'записи', 'записей'],
                        )}`}</div>
                      )}
                    </div>
                  </td>
                )}
                {columns.map(column => {
                  if (isRenderColumn(column)) {
                    return renderTbodyDataCell({ column, row });
                  }

                  return null;
                })}
                {(renderDropdown || onSettingsButtonClick) && (
                  <td className={styles.MenuTBody}>{renderDropdown && renderDropdown(row)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </StickyContainer>
    </table>
  );
  const RenderedTable = TableWithExternalScroll(TableElement);

  return (
    <Fragment>
      <RenderedTable />
      {handleLoadClick && totalCount && data.length < totalCount ? (
        <div className={styles.LoadButtonWrapper}>
          <Button type="button" text="Показать еще" color="load" onClick={handleLoadClick} />
        </div>
      ) : undefined}
    </Fragment>
  );
}

const mapStateToProps = (state: IRootState) => ({
  isErknm: getLawInterface(state) === LawInterfacesEnum.FZ_248,
  isOmbudsman: getUserRoleIsOmbudsman(state),
  isProsecutor: getUserRoleIsProsecutor(state),
  isSuperProsecutor: getUserRoleIsSuperProsecutor(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  knoOrganisations: getAccountOrganizationsData(state).kno,
  announceHeight: getAnnounceHeight(state),
});

export function Table<T extends IData>({ ...props }: ITableProps<T>) {
  const currentProps = ({ ...props } as unknown) as ITableProps<IData>;
  const ConnectedComponent = connect(mapStateToProps)(TableWithoutStore);

  return <ConnectedComponent {...currentProps} />;
}
